import { useEffect, useState } from 'react';
import apiClient from '../../../../services/api-client';
import { teamsService } from '../../../../services/teams.service';
import { TicketType, Team, Agent, CustomField } from '../types';

export function useOrgTicketTypes() {
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get('/ticket-types?active=true')
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data) ? data : data?.data ?? [];
        setTicketTypes(
          items.map((t: any) => ({
            id: t.id,
            name: t.name,
            slug: t.slug,
            color: t.color,
            icon: t.icon,
          })),
        );
      })
      .catch(() => setTicketTypes([]))
      .finally(() => setLoading(false));
  }, []);

  return { ticketTypes, loading };
}

/** Page size for /teams (tenant-service caps a page at 200). */
const TEAMS_PAGE_SIZE = 200;
/** Safety stop: 25 pages = 5,000 teams. */
const TEAMS_MAX_PAGES = 25;

/**
 * The organisation's active TEAMS (Settings → Teams), for "Assign to team".
 *
 * This used to call /team-members — the list of USERS — and label each person
 * as a team, so the picker showed agents and saved a user's id as `teamId`,
 * which the workflow engine then wrote onto the ticket as its team. It now
 * reads the real teams, every page, active only, sorted by name.
 */
export async function loadOrgTeams(): Promise<Team[]> {
  const all: Team[] = [];
  const seen = new Set<string>();
  for (let page = 1; page <= TEAMS_MAX_PAGES; page++) {
    const res = await teamsService.getTeams({ isActive: true, page, limit: TEAMS_PAGE_SIZE });
    const rows = res?.data ?? [];
    for (const t of rows) {
      if (!t?.id || seen.has(t.id)) continue;
      seen.add(t.id);
      all.push({ id: t.id, name: t.name });
    }
    const total = typeof res?.total === 'number' ? res.total : 0;
    if (rows.length < TEAMS_PAGE_SIZE || page * TEAMS_PAGE_SIZE >= total) break;
  }
  return all.sort((a, b) => a.name.localeCompare(b.name));
}

export function useOrgTeams() {
  const [teams, setTeams] = useState<Team[]>([]);
  // True once the list has loaded (successfully or not), so callers can tell
  // "still loading" apart from "this saved team no longer exists".
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadOrgTeams()
      .then((list) => {
        if (!cancelled) setTeams(list);
      })
      .catch(() => {
        if (!cancelled) setTeams([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { teams, loaded };
}

export function useOrgAgents() {
  const [agents, setAgents] = useState<Agent[]>([]);

  useEffect(() => {
    apiClient
      .get('/team-members?role=agent')
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data) ? data : data?.data ?? [];
        setAgents(
          items.map((a: any) => ({
            id: a.id,
            name: a.displayName || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email,
            email: a.email,
          })),
        );
      })
      .catch(() => setAgents([]));
  }, []);

  return { agents };
}

export function useOrgCustomFields(entity: string = 'ticket') {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get('/field-definitions?isActive=true')
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data) ? data : data?.data ?? [];
        setCustomFields(
          items.map((f: any) => ({
            id: f.id,
            name: f.name || f.label,
            key: f.key || f.fieldKey || f.id,
            fieldType: f.fieldType || f.type || 'text',
          })),
        );
      })
      .catch(() => setCustomFields([]))
      .finally(() => setLoading(false));
  }, [entity]);

  return { customFields, loading };
}

export interface ExtractionSchema {
  id: string;
  name: string;
  ticketTypeId?: string;
  ticketTypeName?: string;
  fieldsCount: number;
}

export function useExtractionSchemas() {
  const [schemas, setSchemas] = useState<ExtractionSchema[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get('/extraction-schemas')
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data) ? data : data?.data ?? [];
        setSchemas(
          items.map((s: any) => ({
            id: s.id,
            name: s.name,
            ticketTypeId: s.ticketTypeId,
            ticketTypeName: s.ticketType?.name,
            fieldsCount: s.fields?.length || 0,
          })),
        );
      })
      .catch(() => setSchemas([]))
      .finally(() => setLoading(false));
  }, []);

  return { schemas, loading };
}

export interface OrgPipeline {
  id: string;
  name: string;
}

export interface OrgStage {
  id: string;
  name: string;
}

/** Load the org's ticket pipelines (for the "Move to pipeline stage" action). */
export function useOrgPipelines() {
  const [pipelines, setPipelines] = useState<OrgPipeline[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get('/tickets/pipelines')
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data) ? data : data?.data ?? [];
        setPipelines(items.map((p: any) => ({ id: p.id, name: p.name })));
      })
      .catch(() => setPipelines([]))
      .finally(() => setLoading(false));
  }, []);

  return { pipelines, loading };
}

export interface OrgStageOption {
  id: string;
  name: string;
  pipelineId: string;
  pipelineName: string;
}

/**
 * Every stage of every pipeline, labelled "Pipeline / Stage" — for conditions
 * on `entity.stageId` (If / Branch, Switch). Loaded once per mount.
 */
export function useOrgAllStages() {
  const [stages, setStages] = useState<OrgStageOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/tickets/pipelines');
        const data = res.data;
        const pipelines: OrgPipeline[] = (Array.isArray(data) ? data : data?.data ?? []).map((p: any) => ({
          id: p.id,
          name: p.name,
        }));
        const perPipeline = await Promise.all(
          pipelines.map(async (p) => {
            try {
              const r = await apiClient.get(`/tickets/pipelines/${p.id}/stages`);
              const items = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
              return items.map((s: any) => ({
                id: s.id,
                name: String(s.name ?? '').trim(),
                pipelineId: p.id,
                pipelineName: p.name,
              }));
            } catch {
              return [];
            }
          }),
        );
        if (!cancelled) setStages(perPipeline.flat());
      } catch {
        if (!cancelled) setStages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { stages, loading };
}

/** Load the stages of a given pipeline (re-fetches when the pipeline changes). */
export function useOrgStages(pipelineId?: string) {
  const [stages, setStages] = useState<OrgStage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pipelineId) {
      setStages([]);
      return;
    }
    setLoading(true);
    apiClient
      .get(`/tickets/pipelines/${pipelineId}/stages`)
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data) ? data : data?.data ?? [];
        setStages(items.map((s: any) => ({ id: s.id, name: s.name })));
      })
      .catch(() => setStages([]))
      .finally(() => setLoading(false));
  }, [pipelineId]);

  return { stages, loading };
}
